import { useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Card, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { ApiRequestError } from '../../lib/worker-api.ts';
import { SettingsTabs } from '../settings/SettingsTabs.tsx';
import { InviteForm } from './InviteForm.tsx';
import { InviteLinkBox } from './InviteLinkBox.tsx';
import { MemberList } from './MemberList.tsx';
import {
  type Member,
  useInvite,
  useMembersQuery,
  useReissueLink,
  useUpdateMember,
} from './use-members.ts';

function messageOf(err: unknown): string {
  return err instanceof ApiRequestError || err instanceof Error
    ? err.message
    : 'Something went wrong.';
}

export function MembersPage() {
  const { workspaceId, userId } = useWorkspaceClaims();
  const members = useMembersQuery(workspaceId);
  const invite = useInvite(workspaceId);
  const update = useUpdateMember(workspaceId);
  const reissue = useReissueLink(workspaceId);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newLink, setNewLink] = useState<{ name: string; link: string } | null>(null);

  async function run(member: Member, fn: () => Promise<void>) {
    setBusyId(member.membershipId);
    setActionError(null);
    setNotice(null);
    setNewLink(null);
    try {
      await fn();
    } catch (err) {
      setActionError(messageOf(err));
    } finally {
      setBusyId(null);
    }
  }

  const label = (m: Member) => m.fullName || m.email;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Members"
        description="Changes to roles and access take effect the next time that person's session refreshes (within an hour)."
      />
      <SettingsTabs />

      <Card title="Invite someone">
        <InviteForm
          onInvite={(body) => invite.mutateAsync(body)}
          busy={invite.isPending}
          error={invite.isError ? messageOf(invite.error) : null}
        />
      </Card>

      <section className="flex flex-col gap-3" aria-label="Member list">
        <h2 className="headline text-ink">People</h2>
        {actionError ? <Alert tone="error">{actionError}</Alert> : null}
        {notice ? <Alert tone="success">{notice}</Alert> : null}
        {newLink ? <InviteLinkBox name={newLink.name} link={newLink.link} /> : null}
        {members.isPending ? <Spinner label="Loading members" /> : null}
        {members.isError ? <Alert tone="error">{members.error.message}</Alert> : null}
        {members.data ? (
          <MemberList
            members={members.data}
            actions={{
              currentUserId: userId,
              busyId,
              onRoleChange: (m, role) =>
                void run(m, async () => {
                  await update.mutateAsync({ membershipId: m.membershipId, patch: { role } });
                  setNotice(`${label(m)} is now ${role === 'admin' ? 'an admin' : 'a rep'}.`);
                }),
              onStatusChange: (m, status) => {
                const verb =
                  status === 'active'
                    ? 'Enable'
                    : m.status === 'invited'
                      ? 'Revoke the invite for'
                      : 'Disable';
                if (status === 'disabled' && !window.confirm(`${verb} ${label(m)}?`)) return;
                void run(m, async () => {
                  await update.mutateAsync({ membershipId: m.membershipId, patch: { status } });
                  setNotice(
                    status === 'active'
                      ? `${label(m)} can sign in again.`
                      : `${label(m)} no longer has access (takes effect within an hour).`,
                  );
                });
              },
              onNewLink: (m) =>
                void run(m, async () => {
                  const res = await reissue.mutateAsync(m.userId);
                  if (res.inviteLink) setNewLink({ name: label(m), link: res.inviteLink });
                  else setNotice(`A new invite was emailed to ${m.email}.`);
                }),
            }}
          />
        ) : null}
      </section>
    </div>
  );
}
